import {ComponentType, lazy} from 'react'

// A deploy replaces every hashed chunk (ADR-0052). A tab opened before it still
// runs the old main bundle, so the first page it lazy-loads afterwards asks for a
// chunk that is gone; a 429 from a busy instance fails the same way. The page
// would stay blank, so the tab reloads once and picks up the new build. The
// stamp keeps a chunk that is really broken from reloading in a loop.
export const RELOAD_STAMP_KEY = 'sm-chunk-reload-at'
export const RELOAD_WINDOW_MS = 10_000

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false
  const {name, message} = error as {name?: unknown; message?: unknown}
  if (name === 'ChunkLoadError') return true
  return typeof message === 'string' && /Loading (CSS )?chunk [^ ]+ failed/i.test(message)
}

const readStamp = (): number => {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_STAMP_KEY)) || 0
  } catch {
    return 0
  }
}

const writeStamp = (at: number): boolean => {
  try {
    window.sessionStorage.setItem(RELOAD_STAMP_KEY, String(at))
    return true
  } catch {
    return false
  }
}

// True when the tab is being reloaded; false when it already reloaded inside the
// window (or cannot remember that it did), so the error should surface instead.
export const reloadForChunkError = (
  now: number = Date.now(),
  reload: () => void = () => window.location.reload()
): boolean => {
  if (now - readStamp() < RELOAD_WINDOW_MS) return false
  if (!writeStamp(now)) return false
  reload()
  return true
}

export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{default: T}>,
  reload?: () => void
) {
  return lazy(() =>
    factory().catch((error: unknown) => {
      if (isChunkLoadError(error) && reloadForChunkError(Date.now(), reload)) {
        // Keep the loader up while the page reloads instead of flashing an error.
        return new Promise<{default: T}>(() => {})
      }
      throw error
    })
  )
}
